import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError, RedirectRequest } from '@azure/msal-browser';
import { DEFAULT_GRAPH_SCOPES } from './msal.graph.dynamic.config';

export class GraphInteractionRequiredError extends Error {
  constructor() {
    super('MICROSOFT_INTERACTION_REQUIRED');
  }
}

@Injectable({ providedIn: 'root' })
export class GraphAuthService {
  private initialized = false;
  private readonly SP_ACCOUNT_KEY = 'spHomeAccountId';

  constructor(private readonly msal: MsalService) {}

  async processRedirectResultSafe(): Promise<void> {
    await this.handleRedirectIfPresent().catch(() => null);
    const msalReturnUrl = localStorage.getItem('msalReturnUrl');
    if (msalReturnUrl) {
      // Evita bucles: solo redirige si NO estamos ya en esa URL.
      if (window.location.href !== msalReturnUrl) {
        localStorage.removeItem('msalReturnUrl');
        window.location.href = msalReturnUrl;
        return;
      }
      // Ya estamos en la returnUrl -> limpiar la marca
      localStorage.removeItem('msalReturnUrl');
    }
  }

  async handleRedirectIfPresent(): Promise<AuthenticationResult | null> {
    await this.ensureInitialized();
    const res = await this.msal.instance.handleRedirectPromise().catch(() => null);
    // Tras un redirect correcto, MSAL devuelve account. Guardamos y activamos.
    if (res?.account) {
      this.setActiveAccount(res.account);
    } else {
      // Si no hay res, puede haber cuentas en caché (refresh/navegación)
      this.restoreActiveAccountFromCache();
    }
    return res ?? null;
  }

  async getGraphAccessTokenSilentOnly(scopes: string[] = DEFAULT_GRAPH_SCOPES): Promise<string> {
    await this.ensureInitialized();
    try {
      const account = this.getAccountForSharePoint();
      if (!account) {
        // Sin cuenta: no podemos hacer silent, forzamos interacción.
        throw new GraphInteractionRequiredError();
      }
      const res = await this.msal.instance.acquireTokenSilent({ scopes, account });
      return res.accessToken;
    } catch (e: any) {
      if (e instanceof GraphInteractionRequiredError) {
        throw e;
      }

      if (e instanceof InteractionRequiredAuthError || this.looksLikeInteractionRequired(e)) {
        throw new GraphInteractionRequiredError();
      }

      const code = String(e?.errorCode ?? '').toLowerCase();

      // Error típico que causa bucle: no_account_error
      if (code.includes('no_account') || code.includes('no_account_error')) {
        throw new GraphInteractionRequiredError();
      }

      throw e;
    }
  }

  loginRedirect(scopes: string[] = DEFAULT_GRAPH_SCOPES): void {
    const req: RedirectRequest = {
      scopes,
      prompt: 'select_account',
    };
    this.msal.loginRedirect(req);
  }

  acquireTokenRedirect(scopes: string[] = DEFAULT_GRAPH_SCOPES): void {
    const req: RedirectRequest = {
      scopes,
      prompt: 'select_account',
    };
    this.msal.instance.acquireTokenRedirect(req);
  }

  // =========================
  // Helpers
  // =========================
  private async ensureInitialized(): Promise<void> {
    if (this.initialized) return;
    await this.msal.instance.initialize();
    this.initialized = true;
  }

  private getAccountForSharePoint(): AccountInfo | null {
    const active = this.msal.instance.getActiveAccount();
    if (active) return active;
    const accounts = this.msal.instance.getAllAccounts();
    if (!accounts?.length) return null;
    const preferredHomeAccountId = localStorage.getItem(this.SP_ACCOUNT_KEY);
    if (preferredHomeAccountId) {
      const match = accounts.find((a) => a.homeAccountId === preferredHomeAccountId);
      if (match) return match;
    }
    if (accounts.length === 1) return accounts[0];
    return null;
  }

  private restoreActiveAccountFromCache(): void {
    const account = this.getAccountForSharePoint();
    if (account) this.setActiveAccount(account);
  }

  private setActiveAccount(account: AccountInfo): void {
    this.msal.instance.setActiveAccount(account);
    if (account?.homeAccountId) {
      localStorage.setItem(this.SP_ACCOUNT_KEY, account.homeAccountId);
    }
  }

  private looksLikeInteractionRequired(e: any): boolean {
    const errorMessage = String(e?.errorMessage ?? e?.message ?? '').toLowerCase();
    const errorCode = String(e?.errorCode ?? '').toLowerCase();
    const subError = String(e?.subError ?? '').toLowerCase();

    // El iframe silencioso agotó el tiempo de espera. El error real de AAD queda atrapado
    // dentro del iframe por same-origin policy, por lo que MSAL nunca propaga el
    // invalid_grant subyacente. Tratamos el timeout como interacción requerida.
    if (errorCode === 'monitor_window_timeout') {
      return true;
    }

    return InteractionRequiredAuthError.isInteractionRequiredError(errorCode, errorMessage, subError);
  }
}
